import { Combobox, Transition } from "@headlessui/react";
import clsx from "clsx";
import debounce from "lodash/debounce";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useWorkspaceActiveCustomFields } from "@web-app/api";

// SQL Keywords for highlighting and autocomplete
const SQL_KEYWORDS = [
  "SELECT",
  "FROM",
  "WHERE",
  "GROUP BY",
  "ORDER BY",
  "LIMIT",
  "OFFSET",
  "AND",
  "OR",
  "NOT",
  "IN",
  "AS",
  "ON",
  "JOIN",
  "LEFT",
  "RIGHT",
  "INNER",
  "OUTER",
  "HAVING",
  "DISTINCT",
  "BETWEEN",
  "LIKE",
  "IS",
  "NULL",
  "ASC",
  "DESC",
];

const SQL_FUNCTIONS = [
  "COUNT",
  "SUM",
  "AVG",
  "MIN",
  "MAX",
  "COALESCE",
  "DATE",
  "HOUR",
  "WEEK",
  "MONTH",
  "QUARTER",
  "YEAR",
];

// Date-part functions group (and, in a WHERE clause, filter) a time field by
// granularity instead of aggregating it - they get their own description in
// buildSuggestions rather than the generic "aggregation function" text.
const DATE_GRANULARITY_FUNCTIONS: Record<string, string> = {
  DATE: "day",
  HOUR: "hour",
  MONTH: "month",
  QUARTER: "quarter",
  WEEK: "week",
  YEAR: "year",
};

// Pre-defined Cube.js measures that can be used directly in SQL
// These map to measures defined in orders.yml on the backend
const SQL_MEASURES = [
  { description: "Total number of orders", name: "count" },
  { description: "Total items across all orders", name: "total_items" },
  { description: "Average items per order", name: "avg_items_per_order" },
  { description: "Total order value", name: "total_value" },
  { description: "Average order value", name: "avg_value" },
  { description: "Total tips", name: "total_tip" },
  { description: "Average tip per order", name: "avg_tip" },
  { description: "Total revenue (value + tips)", name: "total_revenue" },
  { description: "Total distance in miles", name: "total_distance_covered" },
  { description: "Average distance per order", name: "avg_distance_covered" },
  { description: "Total service time in seconds", name: "total_service_time" },
  { description: "Average service time per order", name: "avg_service_time" },
  {
    description: "Avg pickup to dropoff time",
    name: "avg_pickup_to_dropoff_duration",
  },
  { description: "Avg start delay", name: "avg_scheduled_to_start_delay" },
  { description: "Avg total order duration", name: "avg_total_order_duration" },
  { description: "Number of completed orders", name: "completed_count" },
  { description: "Number of failed orders", name: "failed_count" },
  { description: "Number of cancelled orders", name: "cancelled_count" },
  { description: "Number of in-progress orders", name: "in_progress_count" },
  { description: "Percentage of completed orders", name: "completion_rate" },
  {
    description: "Pickup on-time in-full percentage",
    name: "pickup_otif_rate",
  },
  { description: "Orders with on-time pickup", name: "pickup_on_time_count" },
  { description: "Orders with late pickup", name: "pickup_late_count" },
  {
    description: "Dropoff on-time in-full percentage",
    name: "dropoff_otif_rate",
  },
  { description: "Orders with on-time dropoff", name: "dropoff_on_time_count" },
  { description: "Orders with late dropoff", name: "dropoff_late_count" },
  { description: "Percentage of late pickups", name: "pickup_late_rate" },
  { description: "Percentage of late dropoffs", name: "dropoff_late_rate" },
  { description: "Pickup-only orders", name: "pickup_only_count" },
  { description: "Dropoff-only orders", name: "dropoff_only_count" },
  {
    description: "Pickup and dropoff orders",
    name: "pickup_and_dropoff_count",
  },
  {
    description: "Orders requiring signature",
    name: "signature_required_count",
  },
  {
    description: "Orders requiring proof of delivery",
    name: "proof_of_delivery_required_count",
  },
  { description: "Orders assigned to routes", name: "routed_orders_count" },
  { description: "Orders not in routes", name: "unrouted_orders_count" },
];

// All SQL columns from the analytics schema (excluding workspace-related)
const SQL_COLUMNS = [
  // Identifiers
  { description: "Primary key", name: "id" },
  { description: "Display order ID", name: "order_id" },
  { description: "External system ID", name: "external_id" },
  { description: "Route ID if assigned to a route", name: "route_id" },
  { description: "Name of the route", name: "route_name" },
  { description: "Position in the route", name: "index_in_route" },
  { description: "ID of assigned driver", name: "assignee_id" },
  { description: "Name of assigned driver", name: "assignee" },

  // Order details
  {
    description: "Pickup, Dropoff, or Pickup and Dropoff",
    name: "delivery_type",
  },
  {
    description:
      "Order status (To Do, Assigned, In Progress, Complete, Failed, Cancelled)",
    name: "status",
  },
  { description: "Order priority (Low, Normal, High)", name: "priority" },
  { description: "Order description", name: "description" },
  { description: "Number of items in the order", name: "items_count" },
  { description: "Expected service time in minutes", name: "service_time" },
  { description: "Required vehicle type", name: "vehicle_type" },
  { description: "Custom metadata JSON", name: "metadata" },

  // Financial
  { description: "Order value", name: "value" },
  { description: "Tip amount", name: "tip" },

  // Distance
  { description: "Distance string", name: "distance" },
  { description: "Actual distance covered in miles", name: "distance_covered" },

  // Pickup details
  { description: "Pickup address", name: "pickup_address" },
  { description: "Pickup contact name", name: "pickup_name" },
  { description: "Pickup phone number", name: "pickup_phone" },
  { description: "Pickup business name", name: "pickup_business_name" },
  { description: "Pickup notes", name: "pickup_note" },
  { description: "Pickup location state/region", name: "pickup_state" },

  // Dropoff details
  { description: "Dropoff address", name: "dropoff_address" },
  { description: "Recipient name", name: "recipient_name" },
  { description: "Recipient phone", name: "recipient_phone" },
  { description: "Recipient business name", name: "recipient_business_name" },
  { description: "Recipient notes", name: "recipient_note" },
  { description: "Dropoff location state/region", name: "dropoff_state" },

  // Requirements
  {
    description: "Requires signature on dropoff",
    name: "signature_requirement",
  },
  {
    description: "Requires photo on dropoff",
    name: "proof_of_delivery_requirement",
  },
  { description: "Requires notes on dropoff", name: "notes_requirement" },
  {
    description: "Requires signature on pickup",
    name: "pickup_signature_requirement",
  },
  {
    description: "Requires photo on pickup",
    name: "pickup_proof_of_pickup_requirement",
  },
  { description: "Requires notes on pickup", name: "pickup_notes_requirement" },

  // Timestamps - Scheduled
  { description: "Scheduled start time", name: "scheduled_start_time" },
  { description: "Scheduled end time", name: "scheduled_end_time" },
  { description: "Estimated time of arrival", name: "estimated_eta" },

  // Timestamps - Actual
  { description: "When driver started moving", name: "in_transit" },
  { description: "When driver arrived at pickup", name: "pickup_arrival_time" },
  { description: "When pickup was completed", name: "actual_pickup_time" },
  {
    description: "When driver arrived at dropoff",
    name: "dropoff_arrival_time",
  },
  { description: "When dropoff was completed", name: "actual_dropoff_time" },

  // Status details
  { description: "Notes about status change", name: "status_notes" },
  {
    description: "Reason for status (e.g., failure reason)",
    name: "status_reasons",
  },

  // System timestamps
  { description: "When the order was created", name: "created_at" },
  { description: "When the order was last updated", name: "updated_at" },
  { description: "Order timezone", name: "timezone" },
];

// Enum values from the analytics schema
const ENUM_VALUES = {
  delivery_type: [
    { label: "Pickup", value: "'Pickup'" },
    { label: "Dropoff", value: "'Dropoff'" },
    { label: "Pickup and Dropoff", value: "'Pickup and Dropoff'" },
  ],
  priority: [
    { label: "Low", value: "'Low'" },
    { label: "Normal", value: "'Normal'" },
    { label: "High", value: "'High'" },
  ],
  status: [
    { label: "To Do", value: "'To Do'" },
    { label: "Assigned", value: "'Assigned'" },
    { label: "In Progress", value: "'In Progress'" },
    { label: "Complete", value: "'Complete'" },
    { label: "Failed", value: "'Failed'" },
    { label: "Cancelled", value: "'Cancelled'" },
  ],
};

// SQL validation errors
interface SQLValidationError {
  column?: number;
  line?: number;
  message: string;
}

// Basic SQL validation (client-side)
function validateSQL(sql: string): SQLValidationError | null {
  if (!sql.trim()) return null;

  const sqlUpper = sql.toUpperCase().trim();
  const sqlLower = sql.toLowerCase().trim();

  // Check for SELECT
  if (!sqlUpper.startsWith("SELECT")) {
    return { message: "Query must start with SELECT" };
  }

  // Check for FROM
  if (!sqlUpper.includes("FROM")) {
    return { message: "Query must include FROM clause" };
  }

  // Check for unclosed string quotes
  const singleQuotes = (sql.match(/'/g) || []).length;
  if (singleQuotes % 2 !== 0) {
    return { message: "Unclosed string quote (')" };
  }

  // Check for unclosed parentheses
  const openParens = (sql.match(/\(/g) || []).length;
  const closeParens = (sql.match(/\)/g) || []).length;
  if (openParens !== closeParens) {
    return {
      message:
        openParens > closeParens
          ? "Unclosed parenthesis - missing ')'"
          : "Extra closing parenthesis ')'",
    };
  }

  // Check for GROUP BY without aggregation
  if (sqlUpper.includes("GROUP BY")) {
    const hasAggregation = SQL_FUNCTIONS.some((fn) => sqlUpper.includes(fn));
    const hasMeasure = SQL_MEASURES.some((m) => sqlLower.includes(m.name));
    if (!hasAggregation && !hasMeasure) {
      return {
        message:
          "GROUP BY requires an aggregation function (COUNT, SUM, AVG, etc.) or a measure",
      };
    }
  }

  // ===== UNSUPPORTED PATTERN DETECTION =====

  // Check for arithmetic expressions between fields (e.g., field1 / field2, field * 100)
  // Look for patterns like: word operator word (outside of function calls and string literals)
  const selectClauseMatch = sql.match(/select\s+(.+?)\s+from/i);
  if (selectClauseMatch) {
    const selectClause = selectClauseMatch[1];
    // Remove string literals for checking
    const selectWithoutStrings = selectClause.replace(/'[^']*'/g, "");
    // Check for arithmetic operators between identifiers/numbers
    if (/\w+\s*[+\-*/]\s*\w+/.test(selectWithoutStrings)) {
      // Make sure it's not inside a function like COUNT(*)
      const withoutFunctions = selectWithoutStrings.replace(
        /\b(COUNT|SUM|AVG|MIN|MAX)\s*\([^)]*\)/gi,
        "",
      );
      if (/\w+\s*[+\-*/]\s*\w+/.test(withoutFunctions)) {
        return {
          message:
            "Arithmetic expressions (/, *, +, -) are not supported. Use pre-defined measures like pickup_otif_rate instead.",
        };
      }
    }
  }

  // Check for CASE WHEN expressions
  if (/\bCASE\s+WHEN\b/i.test(sql)) {
    return {
      message:
        "CASE WHEN expressions are not supported. Use pre-defined measures or columns instead.",
    };
  }

  // Check for UNION
  if (/\bUNION\b/i.test(sql)) {
    return {
      message: "UNION queries are not supported.",
    };
  }

  // Check for subqueries (SELECT inside parentheses)
  if (/\(\s*SELECT\b/i.test(sql)) {
    return {
      message: "Subqueries are not supported.",
    };
  }

  // Check for JOINs
  if (/\b(LEFT|RIGHT|INNER|OUTER|CROSS|FULL)?\s*JOIN\b/i.test(sql)) {
    return {
      message:
        "JOIN operations are not supported. Only the 'orders' table is available.",
    };
  }

  // Check for HAVING clause
  if (/\bHAVING\b/i.test(sql)) {
    return {
      message: "HAVING clause is not supported.",
    };
  }

  // Check for window functions (OVER clause)
  if (/\bOVER\s*\(/i.test(sql)) {
    return {
      message: "Window functions (OVER) are not supported.",
    };
  }

  // Check for CTEs (WITH clause)
  if (/^\s*WITH\b/i.test(sql)) {
    return {
      message: "Common Table Expressions (WITH clause) are not supported.",
    };
  }

  // Check for unsupported comparison operators in WHERE (>, <, >=, <=, !=, <>)
  // Only = and IN are supported
  const whereMatch = sql.match(
    /where\s+(.+?)(?:\s+group\s+by|\s+order\s+by|\s+limit|\s*$)/i,
  );
  if (whereMatch) {
    const whereClause = whereMatch[1];
    // Remove string literals for checking
    const whereWithoutStrings = whereClause.replace(/'[^']*'/g, "''");
    if (/[<>!]=?|<>/.test(whereWithoutStrings)) {
      return {
        message:
          "Only = and IN comparisons are supported in WHERE clause. Operators like >, <, !=, <> are not supported.",
      };
    }
  }

  return null;
}

// Transform custom field name to postgres-safe slug (matches backend logic)
function customFieldSlug(name: string): string {
  // Step 1: Convert to lowercase
  let slug = name.toLowerCase();

  // Step 2: Replace non-alphanumeric/underscore with underscore (postgresFieldRegex)
  slug = slug.replace(/[^a-z0-9_]+/g, "_");

  // Step 3: Replace consecutive underscores with single underscore
  slug = slug.replace(/_+/g, "_");

  // Step 4: Trim leading/trailing underscores
  slug = slug.replace(/^_+|_+$/g, "");

  return slug;
}

// Build autocomplete suggestions from schema
interface Suggestion {
  category: "dimension" | "enum" | "function" | "keyword" | "measure" | "table";
  description?: string;
  label: string;
  value: string;
}

function buildSuggestions(
  customFields?: Array<{
    description: string;
    name: string;
    type: string;
  }>,
): Suggestion[] {
  const suggestions: Suggestion[] = [];

  // Add table
  suggestions.push({
    category: "table",
    description: "Main orders table",
    label: "orders",
    value: "orders",
  });

  // Add SQL keywords
  SQL_KEYWORDS.forEach((kw) => {
    suggestions.push({
      category: "keyword",
      label: kw,
      value: kw,
    });
  });

  // Add SQL functions
  SQL_FUNCTIONS.forEach((fn) => {
    const granularity = DATE_GRANULARITY_FUNCTIONS[fn];
    suggestions.push({
      category: "function",
      description: granularity
        ? `Group or filter by ${granularity}`
        : `${fn} aggregation function`,
      label: `${fn}()`,
      value: `${fn}()`,
    });
  });

  // Add measures from SQL_MEASURES (pre-defined Cube.js measures)
  SQL_MEASURES.forEach((measure) => {
    suggestions.push({
      category: "measure",
      description: measure.description,
      label: measure.name,
      value: measure.name,
    });
  });

  // Add all SQL columns from the analytics schema
  SQL_COLUMNS.forEach((column) => {
    // Avoid duplicates with measures (e.g., if a column is also a measure)
    if (!suggestions.find((s) => s.value === column.name)) {
      suggestions.push({
        category: "dimension",
        description: column.description,
        label: column.name,
        value: column.name,
      });
    }
  });

  // Add custom fields as dimensions
  if (customFields && customFields.length > 0) {
    customFields.forEach((customField) => {
      // Avoid duplicates
      if (!suggestions.find((s) => s.value === customField.name)) {
        suggestions.push({
          category: "dimension",
          description: `${customField.description} (${customField.type})`,
          label: customField.name, // Just the slug (e.g., customer_name)
          value: customField.name,
        });
      }
    });
  }

  // Add enum values for common fields
  Object.entries(ENUM_VALUES).forEach(([field, values]) => {
    values.forEach((enumVal) => {
      suggestions.push({
        category: "enum",
        description: `${field} value`,
        label: enumVal.label,
        value: enumVal.value,
      });
    });
  });

  return suggestions;
}

// Export for SchemaReference
export { customFieldSlug, ENUM_VALUES, SQL_COLUMNS, SQL_MEASURES };

// Syntax highlighting function
function highlightSQL(sql: string, customFieldNames: string[] = []): string {
  if (!sql) return "";

  // Use placeholder tokens to prevent regex from matching inside already-highlighted HTML
  // Using letters (PH_X_) instead of numbers to avoid number highlighting corrupting placeholders
  const placeholders: string[] = [];
  const placeholder = (content: string): string => {
    const index = placeholders.length;
    placeholders.push(content);
    // Use letter-based index to avoid number regex matching the placeholder index
    const letterIndex = index
      .toString()
      .split("")
      .map((d) => String.fromCharCode(65 + parseInt(d)))
      .join("");
    return `\u2060PH_${letterIndex}_\u2060`;
  };

  let highlighted = sql;

  // Escape HTML first
  highlighted = highlighted
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Highlight comments (-- style) first and replace with placeholder - gray
  highlighted = highlighted.replace(/(--.*?)(?=\n|$)/g, (match) =>
    placeholder(`<span class="text-gray-400 italic">${match}</span>`),
  );

  // Highlight strings (single quotes) and replace with placeholder - green
  highlighted = highlighted.replace(/'([^'\\]|\\.)*'/g, (match) =>
    placeholder(`<span class="text-green-600">${match}</span>`),
  );

  // Highlight numbers - orange (now safe since strings are placeholders)
  highlighted = highlighted.replace(
    /\b(\d+\.?\d*)\b/g,
    '<span class="text-orange-600">$1</span>',
  );

  // Highlight SQL keywords (case-insensitive) - blue
  const keywordPattern = new RegExp(`\\b(${SQL_KEYWORDS.join("|")})\\b`, "gi");
  highlighted = highlighted.replace(
    keywordPattern,
    '<span class="text-blue-600 font-semibold">$1</span>',
  );

  // Highlight SQL functions - purple
  const functionPattern = new RegExp(
    `\\b(${SQL_FUNCTIONS.join("|")})\\b`,
    "gi",
  );
  highlighted = highlighted.replace(
    functionPattern,
    '<span class="text-purple-600 font-semibold">$1</span>',
  );

  // Highlight measure names - blue (same as schema measure color)
  const measureNames = SQL_MEASURES.map((m) => m.name);
  const measurePattern = new RegExp(`\\b(${measureNames.join("|")})\\b`, "g");
  highlighted = highlighted.replace(
    measurePattern,
    '<span class="text-blue-500 font-medium">$1</span>',
  );

  // Highlight column names - teal/cyan
  const columnNames = SQL_COLUMNS.map((col) => col.name);
  const columnPattern = new RegExp(`\\b(${columnNames.join("|")})\\b`, "g");
  highlighted = highlighted.replace(
    columnPattern,
    '<span class="text-teal-600">$1</span>',
  );

  // Highlight custom field names - teal/cyan (same as columns)
  if (customFieldNames.length > 0) {
    const customFieldPattern = new RegExp(
      `\\b(${customFieldNames.join("|")})\\b`,
      "g",
    );
    highlighted = highlighted.replace(
      customFieldPattern,
      '<span class="text-teal-600">$1</span>',
    );
  }

  // Restore placeholders - match the letter-based pattern
  highlighted = highlighted.replace(
    /\u2060PH_([A-J]+)_\u2060/g,
    (_, letterIndex) => {
      // Convert letters back to number index
      const index = letterIndex
        .split("")
        .map((c: string) => (c.charCodeAt(0) - 65).toString())
        .join("");
      return placeholders[parseInt(index, 10)];
    },
  );

  return highlighted;
}

// Get the current word being typed (for filtering suggestions)
function getCurrentWord(text: string, cursorPosition: number): string {
  const beforeCursor = text.slice(0, cursorPosition);
  const match = beforeCursor.match(/[\w.]+$/);
  return match ? match[0] : "";
}

interface SQLEditorProps {
  error?: string; // External error from API
  onChange: (value: string) => void;
  onRegisterInsert?: (insertFn: (text: string) => void) => void;
  placeholder?: string;
  value: string;
}

export default function SQLEditor({
  value,
  onChange,
  placeholder = "SELECT COUNT(*) as count FROM orders",
  error: externalError,
  onRegisterInsert,
}: SQLEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [currentWord, setCurrentWord] = useState("");
  const [cursorPosition, setCursorPosition] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ left: 0, top: 0 });

  // Fetch custom fields from all connected workspaces
  const { customFields } = useWorkspaceActiveCustomFields();

  // Transform custom fields to match schema format
  const transformedCustomFields = useMemo(() => {
    if (!customFields || customFields.length === 0) return [];
    return customFields.map((cf) => ({
      description: cf.name, // Original name as description
      name: customFieldSlug(cf.name), // Just the slug for UI
      type: cf.type, // Keep the type for reference
    }));
  }, [customFields]);

  const allSuggestions = useMemo(
    () => buildSuggestions(transformedCustomFields),
    [transformedCustomFields],
  );

  // Validate SQL on change (debounced)
  const [validationError, setValidationError] =
    useState<SQLValidationError | null>(null);

  // Debounced validation
  useEffect(() => {
    const timer = setTimeout(() => {
      const error = validateSQL(value);
      setValidationError(error);
    }, 300);
    return () => clearTimeout(timer);
  }, [value]);

  // Combined error (external takes priority)
  const displayError = externalError || validationError?.message;

  // Filter suggestions based on current word
  const filteredSuggestions = useMemo(() => {
    if (!currentWord) return allSuggestions.slice(0, 15);
    const lower = currentWord.toLowerCase();
    return allSuggestions
      .filter(
        (s) =>
          s.value.toLowerCase().includes(lower) ||
          s.label.toLowerCase().includes(lower),
      )
      .slice(0, 15);
  }, [currentWord, allSuggestions]);

  // Calculate dropdown position based on cursor
  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    // Create a temporary span to measure text position
    const textBeforeCursor = value.slice(0, cursorPosition);
    const lines = textBeforeCursor.split("\n");
    const currentLineIndex = lines.length - 1;
    const currentLineText = lines[currentLineIndex] || "";

    // Approximate character width (monospace)
    const charWidth = 8.4;
    const lineHeight = 24;

    const left = Math.min(
      currentLineText.length * charWidth,
      container.offsetWidth - 250,
    );
    const top = (currentLineIndex + 1) * lineHeight + 4;

    setDropdownPosition({ left: Math.max(0, left), top });
  }, [value, cursorPosition]);

  // Debounced handler for input changes - only updates state, no autocomplete trigger
  const handleInputChange = useMemo(
    () =>
      debounce((newCursorPos: number) => {
        setCursorPosition(newCursorPos);
        // Don't auto-show autocomplete while typing - only on Ctrl+Space
        setShowAutocomplete(false);
      }, 100),
    [],
  );

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    handleInputChange(e.target.selectionStart || 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl+Space to trigger autocomplete
    if (e.ctrlKey && e.key === " ") {
      e.preventDefault();
      const cursorPos = textareaRef.current?.selectionStart || 0;
      const word = getCurrentWord(value, cursorPos);
      setCurrentWord(word);
      setCursorPosition(cursorPos);
      setShowAutocomplete(true);
      updateDropdownPosition();
      return;
    }

    // Escape to close autocomplete
    if (e.key === "Escape") {
      setShowAutocomplete(false);
      return;
    }

    // Arrow keys and Enter when autocomplete is open - let Combobox handle it
    if (
      showAutocomplete &&
      ["ArrowDown", "ArrowUp", "Enter", "Tab"].includes(e.key)
    ) {
      // Don't prevent default for Tab - let it work naturally
      if (e.key !== "Tab") {
        e.preventDefault();
      }
    }
  };

  const handleSelect = (suggestion: Suggestion | null) => {
    if (!suggestion || !textareaRef.current) return;

    const textarea = textareaRef.current;
    const beforeWord = value.slice(0, cursorPosition - currentWord.length);
    const afterCursor = value.slice(cursorPosition);

    const newValue = beforeWord + suggestion.value + afterCursor;
    onChange(newValue);

    // Move cursor to end of inserted text
    const newCursorPos = beforeWord.length + suggestion.value.length;
    setTimeout(() => {
      textarea.selectionStart = newCursorPos;
      textarea.selectionEnd = newCursorPos;
      textarea.focus();
    }, 0);

    setShowAutocomplete(false);
    setCurrentWord("");
  };

  // Update dropdown position when showing
  useEffect(() => {
    if (showAutocomplete) {
      updateDropdownPosition();
    }
  }, [showAutocomplete, updateDropdownPosition]);

  // Insert text at cursor (for schema reference panel)
  const insertAtCursor = useCallback(
    (text: string) => {
      if (!textareaRef.current) return;

      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      const newValue = value.slice(0, start) + text + value.slice(end);
      onChange(newValue);

      // Move cursor after inserted text
      const newCursorPos = start + text.length;
      setTimeout(() => {
        textarea.selectionStart = newCursorPos;
        textarea.selectionEnd = newCursorPos;
        textarea.focus();
      }, 0);
    },
    [value, onChange],
  );

  // Register the insert function with parent component
  useEffect(() => {
    if (onRegisterInsert) {
      onRegisterInsert(insertAtCursor);
    }
  }, [insertAtCursor, onRegisterInsert]);

  const getCategoryIcon = (category: Suggestion["category"]) => {
    switch (category) {
      case "table":
        return "T";
      case "measure":
        return "M";
      case "dimension":
        return "D";
      case "function":
        return "F";
      case "keyword":
        return "K";
      case "enum":
        return "E";
    }
  };

  const getCategoryColor = (category: Suggestion["category"]) => {
    switch (category) {
      case "table":
        return "bg-amber-100 text-amber-700";
      case "measure":
        return "bg-blue-100 text-blue-700";
      case "dimension":
        return "bg-green-100 text-green-700";
      case "function":
        return "bg-purple-100 text-purple-700";
      case "keyword":
        return "bg-gray-100 text-gray-700";
      case "enum":
        return "bg-pink-100 text-pink-700";
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Syntax highlighted overlay */}
      <div
        className="absolute inset-0 pointer-events-none overflow-hidden rounded-lg border border-transparent p-3 font-mono text-sm whitespace-pre-wrap break-words"
        style={{ lineHeight: "24px" }}
        aria-hidden="true"
      >
        <div
          dangerouslySetInnerHTML={{
            __html:
              highlightSQL(
                value,
                transformedCustomFields.map((cf) => cf.name),
              ) || "&nbsp;",
          }}
        />
      </div>

      {/* Actual textarea (transparent text) */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // Delay hiding to allow click on dropdown
          setTimeout(() => setShowAutocomplete(false), 200);
        }}
        placeholder={placeholder}
        rows={10}
        className={clsx(
          "relative w-full rounded-lg border bg-transparent p-3 font-mono text-sm text-transparent caret-gray-900 outline-none resize-none",
          displayError
            ? "border-red-300 focus:border-red-500 focus:ring-1 focus:ring-red-500"
            : "border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500",
        )}
        style={{ caretColor: "#111827", lineHeight: "24px" }}
        spellCheck={false}
      />

      {/* Autocomplete dropdown */}
      <Transition
        show={showAutocomplete && filteredSuggestions.length > 0}
        as={Fragment}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className="absolute z-50 mt-1 max-h-60 w-72 overflow-auto rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5"
          style={{ left: dropdownPosition.left, top: dropdownPosition.top }}
        >
          <Combobox value={null} onChange={handleSelect}>
            <Combobox.Options static className="py-1">
              {filteredSuggestions.map((suggestion) => (
                <Combobox.Option
                  key={`${suggestion.category}-${suggestion.value}`}
                  value={suggestion}
                  className={({ active }) =>
                    clsx(
                      "cursor-pointer select-none px-3 py-2",
                      active ? "bg-blue-50" : "",
                    )
                  }
                >
                  {({ active }) => (
                    <div className="flex items-center gap-2">
                      <span
                        className={clsx(
                          "flex h-5 w-5 items-center justify-center rounded text-xs font-medium",
                          getCategoryColor(suggestion.category),
                        )}
                      >
                        {getCategoryIcon(suggestion.category)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div
                          className={clsx(
                            "text-sm font-mono truncate",
                            active ? "text-blue-900" : "text-gray-900",
                          )}
                        >
                          {suggestion.label}
                        </div>
                        {suggestion.description && (
                          <div className="text-xs text-gray-500 truncate">
                            {suggestion.description}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Combobox.Option>
              ))}
            </Combobox.Options>
          </Combobox>
        </div>
      </Transition>

      {/* Error message or helper text */}
      {displayError ? (
        <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
          <svg
            className="h-3 w-3"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {displayError}
        </p>
      ) : (
        <p className="mt-1 text-xs text-gray-400">
          Press Ctrl+Space for suggestions
        </p>
      )}
    </div>
  );
}
